/**
 * <ultra-bundle-builder>
 *
 * Quantity steppers per flavor (variant), tier discount display, progress bar, and a single
 * "add everything to the cart" request.
 *
 * Config comes from data attributes on the element (see snippets/ultra-bundle-builder.liquid):
 *   data-tiers            "3:35,6:40,9:45"  → minimum total items : discount percent
 *   data-cta-texts        lines of "minQty:text", supports [price] and [remaining]
 *   data-max-items        total item cap (0 = unlimited)
 *   data-direct-checkout  "true" → go to checkout after adding
 *   data-currency/-locale used for price formatting
 *
 * NOTE: the discount shown here is informational. Create a matching automatic discount in the
 * Shopify admin (for example "Buy X items → Y% off") so checkout applies it for real.
 */
if (!customElements.get('ultra-bundle-builder')) {
  class UltraBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.tiers = this.parseTiers(this.dataset.tiers);
      this.ctaTexts = this.parseCtaTexts(this.dataset.ctaTexts);
      this.maxItems = parseInt(this.dataset.maxItems, 10) || 0;
      this.directCheckout = this.dataset.directCheckout === 'true';
      this.freeShipping = parseInt(this.dataset.freeShipping, 10) || 0;
      this.currency = this.dataset.currency || 'USD';
      this.locale = (this.dataset.locale || document.documentElement.lang || 'en').replace('_', '-');
      this.formatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });

      this.rows = Array.from(this.querySelectorAll('[data-ubb-row]'));
      this.cta = this.querySelector('[data-ubb-cta]');
      this.ctaText = this.querySelector('[data-ubb-cta-text]');
      this.errorEl = this.querySelector('[data-ubb-error]');
      this.offerEl = this.querySelector('[data-ubb-offer]');
      this.originalEl = this.querySelector('[data-ubb-original]');
      this.finalEl = this.querySelector('[data-ubb-final]');
      this.savingsEl = this.querySelector('[data-ubb-savings]');
      this.savedEl = this.querySelector('[data-ubb-saved]');
      this.fillEl = this.querySelector('[data-ubb-progress-fill]');
      this.labelsEl = this.querySelector('[data-ubb-progress-labels]');
      this.badgesEl = this.querySelector('[data-ubb-progress-badges]');
      this.sticky = this.querySelector('[data-ubb-sticky]');
      this.stickyCta = this.querySelector('[data-ubb-sticky-cta]');
      this.stickyText = this.querySelector('[data-ubb-sticky-text]');
      this.stickyOffer = this.querySelector('[data-ubb-sticky-offer]');
      this.planSelect = this.querySelector('[data-ubb-plan]');
      this.purchaseRadios = Array.from(this.querySelectorAll('[data-ubb-frequency] input[type="radio"]'));

      this.quantities = new Map();
      this.rows.forEach((row) => this.bindRow(row));

      this.querySelectorAll('[data-ubb-extra-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const row = select.closest('.ubb-extra').querySelector('[data-ubb-row]');
          const option = select.selectedOptions[0];
          row.dataset.variantId = option.value;
          row.dataset.price = option.dataset.price;
          this.render();
        });
      });

      this.purchaseRadios.forEach((radio) =>
        radio.addEventListener('change', () => {
          if (this.planSelect) this.planSelect.hidden = this.purchaseType !== 'subscribe';
          this.render();
        })
      );

      this.cta?.addEventListener('click', () => this.submit());
      this.stickyCta?.addEventListener('click', () => this.onStickyClick());

      this.buildProgress();
      this.observeCta();
      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* ------------------------------------------------------------ parsing */

    parseTiers(value) {
      return (value || '')
        .split(',')
        .map((pair) => pair.trim().split(':'))
        .map(([qty, percent]) => [parseInt(qty, 10), parseFloat(percent)])
        .filter(([qty, percent]) => qty > 0 && !Number.isNaN(percent))
        .sort((a, b) => a[0] - b[0]);
    }

    parseCtaTexts(value) {
      return (value || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const index = line.indexOf(':');
          return [parseInt(line.slice(0, index), 10), line.slice(index + 1).trim()];
        })
        .filter(([qty]) => !Number.isNaN(qty))
        .sort((a, b) => a[0] - b[0]);
    }

    get minItems() {
      return this.tiers.length ? this.tiers[0][0] : 1;
    }

    get purchaseType() {
      const checked = this.purchaseRadios.find((radio) => radio.checked);
      return checked ? checked.value : 'once';
    }

    /* ------------------------------------------------------------- state */

    bindRow(row) {
      const minus = row.querySelector('[data-ubb-minus]');
      const plus = row.querySelector('[data-ubb-plus]');
      if (!minus || !plus) return;
      this.quantities.set(row, 0);
      minus.addEventListener('click', () => this.change(row, -1));
      plus.addEventListener('click', () => this.change(row, 1));
    }

    get totalItems() {
      let total = 0;
      this.quantities.forEach((qty) => (total += qty));
      return total;
    }

    change(row, delta) {
      const current = this.quantities.get(row) || 0;
      let next = Math.max(0, current + delta);
      if (delta > 0 && this.maxItems && this.totalItems + delta > this.maxItems) {
        this.showError(this.dataset.errorMax);
        return;
      }
      this.showError('');
      this.quantities.set(row, next);
      this.render();
    }

    discountFor(total) {
      let percent = 0;
      this.tiers.forEach(([qty, tierPercent]) => {
        if (total >= qty) percent = tierPercent;
      });
      return percent;
    }

    /* ---------------------------------------------------------- rendering */

    money(cents) {
      return this.formatter.format(cents / 100);
    }

    render() {
      const total = this.totalItems;
      let subtotal = 0;
      this.rows.forEach((row) => {
        const qty = this.quantities.get(row) || 0;
        subtotal += qty * (parseInt(row.dataset.price, 10) || 0);
        const value = row.querySelector('[data-ubb-qty]');
        if (value) value.textContent = qty;
        const minus = row.querySelector('[data-ubb-minus]');
        const plus = row.querySelector('[data-ubb-plus]');
        if (minus) minus.disabled = qty === 0;
        if (plus) plus.disabled = !!this.maxItems && total >= this.maxItems;
        row.classList.toggle('is-selected', qty > 0);
      });

      const percent = this.discountFor(total);
      const finalCents = Math.round(subtotal * (1 - percent / 100));
      const savedCents = subtotal - finalCents;

      this.finalEl.textContent = this.money(finalCents);
      if (this.originalEl) {
        this.originalEl.hidden = percent === 0;
        this.originalEl.textContent = this.money(subtotal);
      }
      if (this.offerEl) {
        this.offerEl.hidden = percent === 0;
        this.offerEl.textContent = `${percent}% off`;
      }
      if (this.savingsEl) {
        this.savingsEl.hidden = percent === 0;
        if (this.savedEl) this.savedEl.textContent = this.money(savedCents);
      }
      if (this.stickyOffer) {
        this.stickyOffer.hidden = percent === 0;
        this.stickyOffer.textContent = `${percent}% off`;
      }

      this.updateProgress(total);
      this.updateCta(total, finalCents);

      if (this.maxItems && total >= this.maxItems) this.showError(this.dataset.errorMax);
      else if (this.errorEl && this.errorEl.textContent === this.dataset.errorMax) this.showError('');
    }

    buildProgress() {
      if (!this.labelsEl || !this.badgesEl || !this.tiers.length) return;
      this.labelsEl.innerHTML = this.tiers.map(([qty]) => `<span>${qty}</span>`).join('');
      this.badgesEl.innerHTML = this.tiers.map(([, percent]) => `<span>${percent}% off</span>`).join('');
    }

    updateProgress(total) {
      if (!this.fillEl || !this.tiers.length) return;
      const count = this.tiers.length;
      let position = 0;
      if (total >= this.tiers[count - 1][0]) {
        position = 1;
      } else if (total >= this.tiers[0][0]) {
        for (let i = 0; i < count - 1; i++) {
          const from = this.tiers[i][0];
          const to = this.tiers[i + 1][0];
          if (total >= from && total < to) {
            position = (i + 1 + (total - from) / (to - from)) / count;
            break;
          }
        }
      } else {
        position = (total / this.tiers[0][0]) / count;
      }
      this.fillEl.style.width = `${Math.round(position * 1000) / 10}%`;

      this.badgesEl?.querySelectorAll('span').forEach((badge, index) => {
        badge.classList.toggle('is-reached', total >= this.tiers[index][0]);
      });
    }

    updateCta(total, finalCents) {
      const ready = total >= this.minItems;
      const remaining = Math.max(0, this.minItems - total);
      let text = this.ctaTexts.length ? this.ctaTexts[0][1] : this.ctaText.textContent;
      for (let i = this.ctaTexts.length - 1; i >= 0; i--) {
        if (total >= this.ctaTexts[i][0]) {
          text = this.ctaTexts[i][1];
          break;
        }
      }
      text = text.replace('[price]', this.money(finalCents)).replace('[remaining]', remaining);

      [this.cta, this.stickyCta].forEach((button) => {
        if (!button) return;
        button.classList.toggle('is-disabled', !ready);
        button.setAttribute('aria-disabled', ready ? 'false' : 'true');
      });
      if (this.ctaText) this.ctaText.textContent = text;
      if (this.stickyText) this.stickyText.textContent = text;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    /* -------------------------------------------------------------- sticky */

    observeCta() {
      if (!this.sticky || !this.cta || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const pastCta = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.hidden = !pastCta;
        },
        { threshold: 0 }
      );
      this.observer.observe(this.cta);
    }

    onStickyClick() {
      if (this.stickyCta.classList.contains('is-disabled')) {
        this.querySelector('.ubb__title')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.submit();
    }

    /* ----------------------------------------------------------- add to cart */

    async submit() {
      if (this.cta.classList.contains('is-disabled') || this.cta.classList.contains('is-loading')) return;

      const subscribe = this.purchaseType === 'subscribe' && this.planSelect;
      const items = [];
      this.rows.forEach((row) => {
        const quantity = this.quantities.get(row) || 0;
        if (!quantity) return;
        const item = { id: parseInt(row.dataset.variantId, 10), quantity };
        if (subscribe && row.dataset.extra !== 'true') item.selling_plan = parseInt(this.planSelect.value, 10);
        items.push(item);
      });
      if (!items.length) return;

      this.cta.classList.add('is-loading');
      this.showError('');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items };
      if (cart && !this.directCheckout) {
        body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const state = await response.json();

        if (!response.ok || state.status) {
          this.showError(state.description || state.message || this.dataset.errorGeneric);
          return;
        }

        if (this.directCheckout) {
          window.location.href = this.dataset.checkoutUrl || '/checkout';
          return;
        }

        if (!cart) {
          window.location.href = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ultra-bundle-builder', cartData: state });
        }
        cart.setActiveElement?.(document.activeElement);
        cart.renderContents(state);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

        this.quantities.forEach((_, row) => this.quantities.set(row, 0));
        this.render();
      } catch (error) {
        console.error(error);
        this.showError(this.dataset.errorGeneric);
      } finally {
        this.cta.classList.remove('is-loading');
      }
    }
  }

  customElements.define('ultra-bundle-builder', UltraBundleBuilder);
}
